import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField, InputSlot } from "@repo/components/src/ui/input";
import { Pressable } from "@repo/components/src/ui/pressable";
import { moneySchema } from "@repo/components/src/utils/yup";
import { AlertCircleIcon } from "lucide-react-native";
import { Controller, FieldValues } from "react-hook-form";
import * as yup from "yup";
import { Text } from "../../ui/text";
import { CURRENCY_SYMBOLS, formatSimpleMoney } from "../../utils/money";
import { isCurrencyCode } from "../../utils/money/currencies";
import {
  caretAfterDigits,
  countDigits,
  removeDigitBehindSeparator,
} from "../affixNumberInput/caretHelpers";
import MeasuredHiddenText from "../affixNumberInput/MeasuredHiddenText";
import { useAutoWidth } from "../affixNumberInput/useAutoWidth";
import {
  bindInputRef,
  clampSelectionToLength,
  focusToEnd,
  useCaretSelection,
} from "../affixNumberInput/useCaretSelection";
import { FormInputProps } from "../types";

type MoneyType = yup.InferType<typeof moneySchema>;

type FormInputMoneyProp<TFieldValues extends FieldValues> = FormInputProps<
  TFieldValues,
  MoneyType
>;

const parseAmount = (text: string): string =>
  text.replace(/\D/g, "").replace(/^0+(?=\d)/, "") || "0";

export default function FormInputMoney<TFieldValues extends FieldValues>(
  props: FormInputMoneyProp<TFieldValues>,
) {
  const { width, onMeasure } = useAutoWidth();
  const { selection, setSelection, onSelectionChange, inputRef } =
    useCaretSelection();
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled,
    className,
    size,
  } = props;
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({
        field: { onChange, value, ref, onBlur, disabled: controlDisabled },
        fieldState: { error },
        formState: { isSubmitting },
      }) => {
        const { currency } = value;
        const isEmpty = !value.amount || value.amount === "0";
        const formattedNumber = formatSimpleMoney(value, { onlyNumber: true });
        const displayedValue = isEmpty ? "" : formattedNumber;
        const measuredText = isEmpty ? (placeholder ?? "") : formattedNumber;

        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={controlDisabled || isSubmitting}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText>{label}</FormControlLabelText>
              </FormControlLabel>
            )}
            <Pressable
              onPress={() =>
                focusToEnd(inputRef, setSelection, displayedValue.length)
              }
              className="cursor-text"
            >
              <Input
                isDisabled={controlDisabled || isSubmitting}
                isInvalid={!!error}
                size={size}
              >
                <InputSlot className="pl-3">
                  <Text className="text-typography-900" size={size}>
                    {isCurrencyCode(currency) ? CURRENCY_SYMBOLS[currency] : ""}
                  </Text>
                </InputSlot>
                <MeasuredHiddenText
                  text={measuredText}
                  size={size}
                  onMeasure={onMeasure}
                />
                <InputField
                  ref={bindInputRef(inputRef, ref)}
                  onBlur={onBlur}
                  placeholder={placeholder}
                  value={displayedValue}
                  selection={clampSelectionToLength(
                    selection,
                    displayedValue.length,
                  )}
                  onSelectionChange={onSelectionChange}
                  onChangeText={(typedText: string) => {
                    const previousText = displayedValue;
                    const newText = removeDigitBehindSeparator(
                      previousText,
                      typedText,
                      selection.start,
                    );
                    const caret = Math.min(
                      Math.max(
                        selection.start +
                          (newText.length - previousText.length),
                        0,
                      ),
                      newText.length,
                    );
                    const digitsBeforeCaret = countDigits(
                      newText.slice(0, caret),
                    );
                    const amount = parseAmount(newText);
                    onChange({ amount, currency });
                    const newFormatted = formatSimpleMoney(
                      { amount, currency },
                      { onlyNumber: true },
                    );
                    const caretPosition = caretAfterDigits(
                      newFormatted,
                      digitsBeforeCaret,
                    );
                    setSelection({ start: caretPosition, end: caretPosition });
                  }}
                  keyboardType={"number-pad"}
                  textContentType={"none"}
                  autoCapitalize={"none"}
                  className="flex-none"
                  style={{ width }}
                />
                <InputSlot className="pr-3">
                  <Text className="text-typography-500" size={size}>
                    {currency}
                  </Text>
                </InputSlot>
              </Input>
            </Pressable>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
